import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Eye } from "lucide-react";
import {
  useGetLeaveBalanceReportQuery,
  useLazyGetLeaveBalanceReportDownloadQuery,
} from "../../store/apis/leave.api";
import type { ILeaveBalanceReportItem } from "../../types/leave.api.types";
import { SearchInput, TableSkeleton, SimpleTooltip } from "../common";
import Badge from "../common/Badge";
import type { TableColumn } from "../common/Table";
import { ColumnSelector } from "../common/Table/ColumnSelector";
import { DataTable, DataTableCard, Pagination2, EmployeeCell } from "../common/Table2";
import { getLeaveValue } from "../../utils/leaveUtils";
import { getAggregateUtilization } from "../../utils/leaveUtils";
import LeaveBalanceDetailModal from "./LeaveBalanceDetailModal";
import { getAvatarGradient } from "../../utils/avatarUtils";
import {
  ReportTabPanelProps,
  useMountedGuard,
  useRegisterExport,
} from "./reportTabTypes";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const getErrorMessage = (error: unknown): string => {
  if (!error) return "";
  if (typeof error === "object" && "data" in error) {
    const data = (error as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Failed to load the leave balance report.";
};

export interface LeaveBalanceReportTabProps extends ReportTabPanelProps {}

const LeaveBalanceReportTab: React.FC<LeaveBalanceReportTabProps> = ({
  onStatsChange,
  registerExport,
  period,
}) => {
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ILeaveBalanceReportItem | null>(null);
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!period?.year) return;
    setYear(period.year);
    setPage(DEFAULT_PAGE);
  }, [period?.year]);

  const canViewLeaveBalance = useHasPermission([
    PERMISSIONS.REPORTS_LEAVE_BALANCE_VIEW,
    PERMISSIONS.REPORTS_LEAVE_BALANCE_MANAGE,
  ]);

  const {
    data: report,
    isLoading,
    isFetching,
    error,
  } = useGetLeaveBalanceReportQuery(
    { page, limit, year, search: search.trim() || undefined },
    { skip: !canViewLeaveBalance },
  );

  const [downloadCsv, { isLoading: isExporting }] =
    useLazyGetLeaveBalanceReportDownloadQuery();

  const mountedRef = useMountedGuard();

  const handleExport = useCallback(async () => {
    try {
      const blob = await downloadCsv({
        year,
        search: search.trim() || undefined,
      }).unwrap();
      if (!mountedRef.current) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leave-balance-report-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download leave balance report:", err);
    }
  }, [downloadCsv, year, search]);

  useRegisterExport(handleExport, registerExport);

  const toggleVisibility = useCallback((key: string) => {
    setHiddenKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const rows = useMemo(() => report?.data ?? [], [report]);
  const total = report?.total ?? 0;

  const summaryValues = useMemo(() => {
    const totalBalance = rows.reduce((sum, r) => sum + (r.totalBalance ?? 0), 0);
    const pending = rows.reduce((sum, r) => sum + (r.pendingRequests ?? 0), 0);
    const avg = rows.length > 0 ? Number((totalBalance / rows.length).toFixed(1)) : 0;
    return {
      employees: report?.total ?? rows.length,
      balance: totalBalance,
      avg,
      pending,
    };
  }, [rows, report]);

  useEffect(() => {
    onStatsChange?.(summaryValues);
  }, [summaryValues, onStatsChange]);

  const dynamicLeaveColumns = useMemo<TableColumn<ILeaveBalanceReportItem>[]>(() => {
    const firstRow = rows[0];
    if (!firstRow || !firstRow.leaveBalances || firstRow.leaveBalances.length === 0) {
      return [
        { key: "annual", title: "Annual", width: 100, render: (_, r) => <span className="text-label font-bold text-[var(--color-text-primary)]">{getLeaveValue(r, "annual")}</span> },
        { key: "sick", title: "Sick", width: 100, render: (_, r) => <span className="text-label font-bold text-[var(--color-text-primary)]">{getLeaveValue(r, "sick")}</span> },
        { key: "casual", title: "Casual", width: 110, render: (_, r) => <span className="text-label font-bold text-[var(--color-text-primary)]">{getLeaveValue(r, "casual")}</span> },
        { key: "comp_off", title: "Comp-Off", width: 110, render: (_, r) => <span className="text-label font-bold text-[var(--color-text-primary)]">{getLeaveValue(r, "comp_off")}</span> },
      ];
    }

    return firstRow.leaveBalances.map((lb) => ({
      key: `lb_${lb.label.toLowerCase().replace(/\s+/g, "_")}`,
      title: lb.label,
      width: Math.max(110, lb.label.length * 10),
      render: (_, record) => {
        const entry = (record.leaveBalances ?? []).find(
          (b) => b.label.toLowerCase() === lb.label.toLowerCase(),
        );
        return (
          <span className="text-label font-bold text-[var(--color-text-primary)]">
            {entry?.value ?? 0}
          </span>
        );
      },
    }));
  }, [rows]);

  const columns = useMemo<TableColumn<ILeaveBalanceReportItem>[]>(() => {
    return [
      {
        key: "employee",
        title: "Employee",
        required: true,
        width: 280,
        render: (_, record) => (
          <EmployeeCell
            name={record.fullName}
            sub={record.workEmail}
            avatarBg={getAvatarGradient(record.fullName || "")}
          />
        ),
      },
      {
        key: "department",
        title: "Department",
        width: 160,
        render: (_, record) => (
          <span className="text-label text-[var(--color-text-secondary)]">
            {record.departmentName || "N/A"}
          </span>
        ),
      },
      ...dynamicLeaveColumns,
      {
        key: "pending",
        title: "Pending Requests",
        width: 150,
        render: (_, record) => {
          const count = record.pendingRequests ?? 0;
          return (
            <span className={`text-label font-bold ${count > 0 ? "text-red-500" : "text-[var(--color-text-primary)]"}`}>
              {count}
            </span>
          );
        },
      },
      {
        key: "totalBalance",
        title: "Total Balance",
        width: 120,
        render: (_, record) => (
          <span className="text-label font-bold text-[var(--color-text-primary)]">
            {record.totalBalance ?? 0}
          </span>
        ),
      },
      {
        key: "used",
        title: "Used",
        width: 100,
        render: (_, record) => {
          const { used } = getAggregateUtilization(record);
          return (
            <span className="text-label font-bold text-[var(--color-text-primary)]">
              {used}
            </span>
          );
        },
      },
      {
        key: "utilization",
        title: "Utilization",
        width: 140,
        render: (_, record) => {
          const { percent, color, bg } = getAggregateUtilization(record);
          const utilWidth = `${Math.min(100, percent)}%`;

          return (
            <div className="flex items-center gap-[9px]">
              <div className="w-24 h-[7px] rounded-full bg-[var(--color-bg-track)] overflow-hidden">
                <div className={`h-full rounded-full ${bg}`} style={{ width: utilWidth }}></div>
              </div>
              <span className={`text-[12px] font-bold ${color}`}>
                {percent}%
              </span>
            </div>
          );
        },
      },
      {
        key: "spacer",
        title: "",
        width: 40,
        render: () => null,
      },
      {
        key: "actions",
        title: "Action",
        align: "right",
        width: 100,
        truncate: false,
        required: true,
        render: (_, record) => (
          <button
            type="button"
            aria-label="View all details"
            onClick={(e) => {
              e.stopPropagation();
              setSelected(record);
            }}
            className="text-body-sm font-bold text-[var(--color-primary-700)] border border-[var(--color-border-primary)] bg-white px-3 py-1.5 rounded-[9px] hover:bg-[var(--color-bg-hover)] transition-colors shadow-xs whitespace-nowrap"
          >
            View all
          </button>
        ),
      },
    ];
  }, [dynamicLeaveColumns]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.required || !hiddenKeys.includes(c.key)),
    [columns, hiddenKeys],
  );

  const optionalVisibleKeys = useMemo(
    () =>
      columns
        .filter((c) => !c.required && !hiddenKeys.includes(c.key))
        .map((c) => c.key),
    [columns, hiddenKeys],
  );

  const errorMessage = getErrorMessage(error);

  return (
    <div className="space-y-5">
      {errorMessage ? (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" aria-hidden />
          <span>{errorMessage}</span>
        </div>
      ) : null}

      <DataTableCard
        title="Leave Balance Report"
        count={total}
        toolbar={
          <>
            <SearchInput
              placeholder="Search employee..."
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(DEFAULT_PAGE);
              }}
              className="w-full sm:w-[220px]"
            />
            <ColumnSelector
              columns={columns}
              visibleKeys={optionalVisibleKeys}
              onToggle={toggleVisibility}
            />
          </>
        }
        pagination={
          <Pagination2
            total={total}
            page={page}
            pageSize={limit}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setLimit(size);
              setPage(DEFAULT_PAGE);
            }}
          />
        }
      >
        <DataTable
          columns={visibleColumns}
          rows={rows}
          rowKey={(r) => r.employeeId || r.fullName}
          loading={isLoading}
          fetching={isFetching}
          skeleton={<TableSkeleton rows={limit} columns={visibleColumns.length} />}
          emptyMessage="No employees found for the selected fiscal year."
          responsive="scroll"
        />
      </DataTableCard>

      <LeaveBalanceDetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        record={selected}
        onExport={handleExport}
        exporting={isExporting}
      />
    </div>
  );
};

export default LeaveBalanceReportTab;